import React, { useEffect, useMemo, useState } from "react";
import { Button, message, Tag, Select } from "antd"; // ⬅️ added Select
import { getRequest, postRequest, putRequest, deleteRequest } from "../api/api";
import TableOne from "../table/TableOne";
import McqsFormModal from "../components/McqsFormModal";
import Swal from "sweetalert2";
import { FaPlus } from "react-icons/fa6";
import { RxDropdownMenu } from "react-icons/rx";
import ReactDOMServer from "react-dom/server";
import { FaTrash, FaTimes } from "react-icons/fa";

const McqsPage = () => {
  const [mcqs, setMcqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMcq, setEditingMcq] = useState(null);

  // NEW: chapter filter state (stores chapter id if present, otherwise the title string)
  const [chapterFilter, setChapterFilter] = useState(null);

  const fetchMcqs = async () => {
    try {
      setLoading(true);
      const res = await getRequest("/mcqs?all=true");
      setMcqs(res);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMcqs();
  }, []);

  const handleDelete = async (record) => {
    const result = await Swal.fire({
      title: `Delete this MCQ?`,
      text: `“${record.question}”`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
       confirmButtonText: ReactDOMServer.renderToString(
    <>
      <FaTrash style={{ marginRight: 6, verticalAlign: "middle" }} /> Yes, delete it!
    </>
  ),
  cancelButtonText: ReactDOMServer.renderToString(
    <>
      <FaTimes style={{ marginRight: 6, verticalAlign: "middle" }} /> Cancel
    </>
  ),
  allowOutsideClick: false,
});

    if (result.isConfirmed) {
      try {
        await deleteRequest(`/mcqs/${record.id}`);
        Swal.fire("Deleted!", "MCQ has been deleted.", "success");
        fetchMcqs();
      } catch (err) {
        Swal.fire("Error!", err.message || "Failed to delete MCQ.", "error");
      }
    }
  };

  const handleEdit = (record) => {
    setEditingMcq(record);
    setModalOpen(true);
  };

  const handleFormSubmit = async (data) => {
    try {
      if (editingMcq) {
        await putRequest(`/mcqs/${editingMcq.id}`, data);
        message.success("MCQ updated successfully!");
      } else {
        await postRequest("/mcqs", data);
        message.success("MCQ added successfully!");
      }
      setModalOpen(false);
      setEditingMcq(null);
      fetchMcqs();
    } catch (err) {
      message.error(err.message);
    }
  };

  // Build unique chapter options from data
  const chapterOptions = useMemo(() => {
    const map = new Map();
    for (const r of mcqs || []) {
      const id = r?.chapter?.id ?? r?.chapter_id ?? r?.chapterId ?? null; // prefer id if present
      const title =
        r?.chapter?.title ??
        r?.chapter_title ??
        (id != null ? `Chapter ${id}` : null);

      if (title) {
        const value = id != null ? String(id) : `title::${title}`; // store id or title token
        if (!map.has(value)) map.set(value, title);
      }
    }
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [mcqs]);

  // Apply chapter filter to data
  const filteredMcqs = useMemo(() => {
    if (!chapterFilter) return mcqs;

    return (mcqs || []).filter((r) => {
      const id = r?.chapter?.id ?? r?.chapter_id ?? r?.chapterId ?? null;
      const title = r?.chapter?.title ?? r?.chapter_title ?? "";
      const filterIsId = !String(chapterFilter).startsWith("title::");

      if (filterIsId) {
        return String(id) === String(chapterFilter);
      } else {
        const wantedTitle = String(chapterFilter).replace(/^title::/, "");
        return String(title).toLowerCase() === wantedTitle.toLowerCase();
      }
    });
  }, [mcqs, chapterFilter]);

  const columns = [
    {
      title: "Sr. No.",
      key: "index",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Chapter",
      key: "chapter_title",
      render: (record) => record.chapter?.title || "—",
    },
    {
      title: "Question",
      dataIndex: "question",
      key: "question",
    },
    {
      title: "A",
      dataIndex: "option_a",
      key: "option_a",
    },
    {
      title: "B",
      dataIndex: "option_b",
      key: "option_b",
    },
    {
      title: "C",
      dataIndex: "option_c",
      key: "option_c",
    },
    {
      title: "D",
      dataIndex: "option_d",
      key: "option_d",
    },
    {
      title: "Correct",
      dataIndex: "correct_answer",
      key: "correct_answer",
      render: (text) => <Tag color="green">{text}</Tag>,
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "status",
      render: (value) => {
        const isActive = value === 1 || value === "1";
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              color: isActive ? "#065f46" : "#991b1b",
              fontWeight: "500",
              backgroundColor: isActive ? "#d1fae5" : "#fee2e2",
              padding: "4px 10px",
              borderRadius: "20px",
            }}
          >
            <span
              style={{
                height: "8px",
                width: "8px",
                borderRadius: "50%",
                backgroundColor: isActive ? "#10b981" : "#ef4444",
                display: "inline-block",
              }}
            />
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
  ];

  return (
    <div className="page-container">
      <div
        className="header-button"
        style={{
          padding: "20px",
          display: "flex",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
   <h2
  style={{
    margin: 0,
    color: "#5a355a",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    lineHeight: 1,
  }}
>
  <RxDropdownMenu />
  <span>MCQs</span>
  <span
    style={{
      color: "#979797ff",
      fontSize: "14px",
      fontWeight: 500,
      alignSelf: "flex-end", // bottom-align just the total
    }}
  >
    (Total: {mcqs?.length ?? 0})
  </span>
</h2>

        {/* RIGHT SIDE: actions */}
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* NEW: Chapter filter with search */}
          {/* <Select
            allowClear
            showSearch
            placeholder="Filter by Chapter"
            value={chapterFilter}
            onChange={(v) => setChapterFilter(v || null)}
            options={chapterOptions}
            style={{ minWidth: 240 }}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          /> */}

          <Button
            type="primary"
            onClick={() => {
              setModalOpen(true);
              setEditingMcq(null);
            }}
          >
            <FaPlus /> Add New MCQ
          </Button>
        </div>
      </div>

      <TableOne
        columns={columns}
        data={filteredMcqs}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        pageName="mcq"
        loading={loading}
        leftExtra={
          <Select
            allowClear
            showSearch
            placeholder="Filter by Chapter"
            value={chapterFilter}
            onChange={(v) => setChapterFilter(v || null)}
            options={chapterOptions}
            style={{ minWidth: 240 }}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        }
      />

      <McqsFormModal
        visible={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditingMcq(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={editingMcq}
        destroyOnClose
      />
    </div>
  );
};

export default McqsPage;
